import { MantineProvider } from '@mantine/core';
import { GlobalMessages } from '@shared/localization/messages';
import type { DataTableColumn } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import { type Control, useWatch } from 'react-hook-form';

import type { EstimationModelResponse } from '@/api/schema/data-contracts';
import { FormattedNumber } from '@/components/atoms/FormattedNumber';
import { SanitizedHtml } from '@/components/atoms/SanitizedHtml';
import { UserFullName } from '@/components/UserFullName';
import { useMessages } from '@/hooks/messages';
import { sortResourceTypes } from '@/utils/sorting';

import { estimationItemDetailEditationMessageDefinitions } from '../../EstimationItemEditation/messages'; // TODO: either move it to common place or create a copy specifically for this component
import type {
  EstimationItemDetail,
  EstimationItemsEditFormModel,
  MethodologyData,
} from '../../model'; // TODO: either move it to common place or create a copy specifically for this component

interface EstimationItemEditationProps {
  control: Control<EstimationItemsEditFormModel>;
  methodologies: MethodologyData[];
  estimationModel: EstimationModelResponse;
  estimationItem: EstimationItemDetail;
  resourceDisplayNames?: Record<string, string>;
}

export const MainEstimationItemDetail: FC<EstimationItemEditationProps> = ({
  control,
  methodologies,
  estimationModel,
  estimationItem,
  resourceDisplayNames,
}) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(estimationItemDetailEditationMessageDefinitions);
  const formModel = useWatch({ control });

  if (!formModel.mainEstimationItem) {
    throw new Error('mainEstimationItem cannot be undefined');
  }

  const allResourceTypes = sortResourceTypes(
    estimationModel.resourceTypes,
    estimationModel.resourceTypeGroups,
  );

  const columns: DataTableColumn<Pick<EstimationItemDetail, 'resources'>>[] = allResourceTypes.map(
    (resourceType) => ({
      accessor: `resource_type_id_${resourceType.id}`,
      title: resourceDisplayNames?.[resourceType.id] ?? resourceType.name,
      textAlign: 'right',
      noWrap: true,
      render: (item) => (
        <FormattedNumber
          value={item.resources?.find((x) => x.resourceTypeId === resourceType.id)?.value}
        />
      ),
    }),
  );

  const hasEstimationModelItemParameters = estimationItem.params.some(
    (x) => x.estimationModelItemParameterId,
  );
  // Not every item's methodology is in this collection: it holds the ones the estimation model
  // links, and an item can reference another (EWM-605 - a converted estimation in TEST did exactly
  // that on all 114 items and killed the Estimation tab through the same assertion).
  const methodologyName = estimationItem.methodologyId
    ? methodologies.find((x) => x.id === estimationItem.methodologyId)?.name
    : undefined;
  return (
    <div className='space-y-5'>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <div>
          <label htmlFor='mainEstimationItem.name'>{messages.phaseName()}</label>
          {formModel.mainEstimationItem.name}
        </div>
        <div>
          <label htmlFor='mainEstimationItem.phaseLeadId'>{messages.phaseLead()}</label>
          <UserFullName userId={estimationItem.phaseLeadId} />
        </div>
      </div>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <div>
          <label htmlFor='mainEstimationItem.description'>{messages.description()}</label>
          <SanitizedHtml htmlString={formModel.mainEstimationItem.description} />
        </div>
        <div className='flex flex-col gap-4'>
          <div>
            <label htmlFor='mainEstimationItem.methodologyId'>{messages.methodology()}</label>
            {hasEstimationModelItemParameters ? globalMessages.notAvailable() : methodologyName}
          </div>
        </div>
      </div>
      <MantineProvider>
        <div className='relative'>
          <div className='pointer-events-none absolute top-0 right-0 z-[5] block h-full w-8 bg-gradient-to-l from-white/80 to-transparent dark:from-black/80' />
          <DataTable<Pick<EstimationItemDetail, 'resources'>>
            highlightOnHover
            withTableBorder
            className='table-hover whitespace-nowrap'
            records={[estimationItem]}
            columns={columns}
          />
        </div>
      </MantineProvider>
    </div>
  );
};
